import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useI18n } from '../../i18n';
import { useAnnounce } from '../../ui/a11y';
import { Pill } from '../../ui/DataBits';
import { Dialog } from '../../ui/Dialog';
import { errorText, rpc, type AppError } from '../admin/api';
import { ComplianceList } from '../compliance/ComplianceList';
import { HeadFrame, type HeadHomeData } from './HeadPages';
import { loc, type Loc } from '../../lib/loc';
import { EvidenceHint, type Evidence } from '../r3/common';


// ── J7 Student council ─────────────────────────────────────────────────────

type CouncilLink = { id: string; weeks: number; created_at: string; expires_at: string; revoked_at: string | null; views: number; active: boolean };
type SchoolMsg = { id: string; body: string; created_at: string; withdrawn_at: string | null };

export function CouncilPage() {
  const { t } = useI18n();
  return (
    <HeadFrame tab="council" title={t['h2.councilTitle']}>
      {(_ctx, home) => <CouncilBody home={home} />}
    </HeadFrame>
  );
}

function CouncilBody({ home }: { home: HeadHomeData }) {
  const { t, locale } = useI18n();
  const announce = useAnnounce();
  const [links, setLinks] = useState<CouncilLink[] | null>(null);
  const [msgs, setMsgs] = useState<SchoolMsg[]>([]);
  const [weeks, setWeeks] = useState(8);
  const [days, setDays] = useState(7);
  const [created, setCreated] = useState<string | null>(null);
  const [body, setBody] = useState('');
  const [err, setErr] = useState<AppError | null>(null);
  const scoped = home.me.scope_grades !== null;
  const fmt = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' });

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([rpc<CouncilLink[]>('head_council_links'), rpc<SchoolMsg[]>('head_school_messages')]);
    if (a.error) setErr(a.error);
    else setLinks(a.data);
    if (!b.error) setMsgs(b.data);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  async function create(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    const r = await rpc<{ token: string }>('head_create_council_link', { p_weeks: weeks, p_days: days });
    if (r.error) return setErr(r.error);
    setCreated(`${window.location.origin}/council/${r.data.token}`);
    load();
  }
  async function revoke(id: string) {
    const r = await rpc('head_revoke_council_link', { p_id: id });
    if (r.error) setErr(r.error);
    else {
      announce(t['h2.linkRevoked']);
      load();
    }
  }
  async function post(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    const r = await rpc('head_post_school_message', { p_body: body });
    if (r.error) return setErr(r.error);
    setBody('');
    announce(t['ts.ysPosted']);
    load();
  }
  async function withdraw(id: string) {
    const r = await rpc('head_withdraw_school_message', { p_id: id });
    if (r.error) setErr(r.error);
    else load();
  }

  if (scoped) {
    return (
      <section className="panel p5">
        <p>{t['h2.councilScoped']}</p>
      </section>
    );
  }

  return (
    <>
      <p className="notice notice-block">{t['h2.councilIntro']}</p>
      {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
      <div className="two-col">
        <section className="panel p5" aria-labelledby="cl-new">
          <h2 className="panel-title" id="cl-new">{t['h2.linkNew']}</h2>
          <form className="form" onSubmit={create} noValidate>
            <div className="form-row">
              <div className="field-group">
                <label className="field-label" htmlFor="cl-weeks">{t['h2.linkWeeks']}</label>
                <div className="select-wrap">
                  <select id="cl-weeks" className="select" value={weeks} onChange={(e) => setWeeks(Number(e.target.value))}>
                    {[4, 8, 12, 26].map((w) => <option key={w} value={w}>{t['h2.nWeeks'](w)}</option>)}
                  </select>
                </div>
              </div>
              <div className="field-group">
                <label className="field-label" htmlFor="cl-days">{t['h2.linkDays']}</label>
                <div className="select-wrap">
                  <select id="cl-days" className="select" value={days} onChange={(e) => setDays(Number(e.target.value))}>
                    {[1, 3, 7, 14].map((d) => <option key={d} value={d}>{t['h2.nDays'](d)}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <p className="form-hint">{t['h2.linkHint']}</p>
            <div className="form-actions"><button type="submit" className="btn btn-primary">{t['h2.linkCreate']}</button></div>
          </form>
        </section>
        <section className="panel p5" aria-labelledby="cl-list">
          <h2 className="panel-title" id="cl-list">{t['h2.linkList']}</h2>
          {links && links.length === 0 && <p className="text-muted">{t['h2.linkNone']}</p>}
          <ul className="sched-list">
            {links?.map((l) => (
              <li key={l.id}>
                <span>
                  {t['h2.nWeeks'](l.weeks)} · {l.active ? t['h2.until'](fmt.format(new Date(l.expires_at))) : <Pill kind="neutral">{t['h2.ended']}</Pill>}
                  <span className="text-muted"> · {t['h2.views'](l.views)}</span>
                </span>
                {l.active && <button type="button" className="link-btn" onClick={() => revoke(l.id)}>{t['h2.revoke']}</button>}
              </li>
            ))}
          </ul>
        </section>
      </div>
      <section className="panel p5" aria-labelledby="sm-title">
        <h2 className="panel-title" id="sm-title">{t['h2.schoolSaid']}</h2>
        <p className="form-hint">{t['h2.schoolSaidHint']}</p>
        {msgs.filter((m) => !m.withdrawn_at).map((m) => (
          <div key={m.id}>
            <blockquote className="ys-quote">{m.body}</blockquote>
            <button type="button" className="link-btn" onClick={() => withdraw(m.id)}>{t['ts.ysWithdraw']}</button>
          </div>
        ))}
        <form className="form" onSubmit={post} noValidate>
          <div className="field-group">
            <label className="field-label" htmlFor="sm-body">{t['h2.schoolSaidLabel']}</label>
            <textarea id="sm-body" className="field" rows={2} maxLength={200} value={body} onChange={(e) => setBody(e.target.value)} aria-describedby="sm-hint" />
            <p className="form-hint" id="sm-hint">{t['ts.ysRule']} {t['ts.charsLeft'](200 - body.length)}</p>
          </div>
          <div className="form-actions"><button type="submit" className="btn btn-ghost" disabled={body.trim().length < 3}>{t['h2.schoolSaidPost']}</button></div>
        </form>
      </section>
      <Dialog open={created !== null} title={t['h2.linkCreatedTitle']} intro={<p>{t['h2.linkCreatedIntro']}</p>}
        hideSubmit closeLabel={t['h2.done']} onClose={() => setCreated(null)}>
        <p className="secret" tabIndex={0}>{created}</p>
        <button type="button" className="btn btn-ghost" onClick={() => created && navigator.clipboard?.writeText(created).then(() => announce(t['h2.copied']))}>
          {t['h2.copy']}
        </button>
      </Dialog>
    </>
  );
}

// ── I3 Playbook (head) ─────────────────────────────────────────────────────

type PbItem = { key: string; topic: string; title: Loc; body: Loc; is_draft: boolean; school_version: boolean; evidence?: Evidence };
type Proposal = { id: string; key: string; topic: string; title: Loc; body: Loc; proposed_by: string | null; created_at: string };

export function HeadPlaybookPage() {
  const { t } = useI18n();
  return (
    <HeadFrame tab="playbook" title={t['h2.playbookTitle']}>
      {() => <HeadPlaybookBody />}
    </HeadFrame>
  );
}

function HeadPlaybookBody() {
  const { t, lang } = useI18n();
  const announce = useAnnounce();
  const [data, setData] = useState<{ custom_allowed: boolean; can_decide: boolean; items: PbItem[]; proposals: Proposal[] } | null>(null);
  const [err, setErr] = useState<AppError | null>(null);
  const load = useCallback(async () => {
    const r = await rpc<NonNullable<typeof data>>('head_playbook');
    if (r.error) setErr(r.error);
    else setData(r.data);
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  async function decide(id: string, approve: boolean) {
    const r = await rpc('head_decide_playbook', { p_proposal_id: id, p_approve: approve });
    if (r.error) setErr(r.error);
    else {
      announce(approve ? t['h2.pbApproved'] : t['h2.pbRejected']);
      load();
    }
  }
  if (err && !data) return <p className="notice notice-block" role="alert">{errorText(t, err)}</p>;
  if (!data) return <p role="status">{t.loading}</p>;
  return (
    <>
      <p className="notice notice-block">{data.custom_allowed ? t['h2.pbCustomOn'] : t['h2.pbCustomOff']}</p>
      {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
      {data.custom_allowed && (
        <section className="panel p5" aria-labelledby="pb-props">
          <h2 className="panel-title" id="pb-props">{t['h2.pbProposals']}</h2>
          {data.proposals.length === 0 && <p className="text-muted">{t['h2.pbNoProposals']}</p>}
          <ul className="approval-list">
            {data.proposals.map((p) => (
              <li key={p.id} className="approval">
                <p><strong>{loc(p.title, lang)}</strong> · {t[`topic.${p.topic}` as 'topic.exams'] ?? p.topic}</p>
                <p>{loc(p.body, lang)}</p>
                <p className="text-muted">{t['h2.pbBy'](p.proposed_by ?? '–')}</p>
                {data.can_decide ? (
                  <div className="form-actions">
                    <button type="button" className="btn btn-ghost" onClick={() => decide(p.id, false)}>{t['h2.reject']}</button>
                    <button type="button" className="btn btn-primary" onClick={() => decide(p.id, true)}>{t['h2.approve']}</button>
                  </div>
                ) : (
                  <p className="notice notice-block">{t['h2.pbPrimaryDecides']}</p>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
      <section className="panel p5" aria-labelledby="pb-items">
        <h2 className="panel-title" id="pb-items">{t['h2.pbItems']}</h2>
        <PlaybookList items={data.items} />
      </section>
    </>
  );
}

export function PlaybookList({ items }: { items: PbItem[] }) {
  const { t, lang } = useI18n();
  return (
    <ul className="pb-list">
      {items.map((i) => (
        <li key={i.key}>
          <p className="pb-title">
            <strong>{loc(i.title, lang)}</strong>{' '}
            {i.is_draft && <Pill kind="warn">{t['ts.draft']}</Pill>} {i.school_version && <Pill kind="good">{t['h2.pbSchoolVersion']}</Pill>}
          </p>
          <p className="text-muted">{i.topic === 'any' ? t['h2.pbAnyTopic'] : t[`topic.${i.topic}` as 'topic.exams']}</p>
          <p>{loc(i.body, lang)}</p>
          <EvidenceHint e={i.evidence} />
        </li>
      ))}
    </ul>
  );
}

// ── J2 Compliance (head) ───────────────────────────────────────────────────

export function HeadCompliancePage() {
  const { t } = useI18n();
  return (
    <HeadFrame tab="compliance" title={t['h2.complianceTitle']}>
      {() => <ComplianceList />}
    </HeadFrame>
  );
}
